// src/components/MobileDashboard.jsx
//
// Mobile PWA dashboard for EasyGreeks Analytics.
// - Consumes the SAME state AnalyticsPage.jsx already fetches (data, loading,
//   lastUpdate, liveSpot, liveOptions) — no duplicate API calls.
// - FutureTile.jsx stays untouched (desktop still imports/uses it as-is).
//   The mobile hero section below no longer renders <FutureTile/> directly —
//   it needed a slimmer, mobile-specific futures card (LTP/OI/Vol only), so
//   that's implemented locally as <FuturesHeroCard/> instead of reusing the
//   desktop tile, which shows a lot more (build-up, action, commitment, etc).
// - Everything else here is self-contained so AnalyticsPage.jsx only needs a
//   one-line breakpoint check to use it.
//
// Data note: the top ratio strip in the prototype includes VIX, ADV/DEC and
// MAX PAIN. None of those exist in the current backend payload (server.py /
// analytics_engine.py) yet. Per instruction, we don't skip the UI just
// because the backend doesn't compute it yet — those three cards render as
// placeholders ("—") so the layout matches the prototype today. Once the API
// starts returning adv_dec_ratio / vix / max_pain, wire them into the three
// marked <RatioCard> calls in RatioCardsRow below — no other changes needed.

import React, { useState, useEffect, useRef  } from 'react';
import {
  SlidersHorizontal,
  Moon,
  Sun,
  X,
  Check,
  Share2,
} from 'lucide-react';
import { ANALYTICS_MODULES } from '../config/analyticsModules';

/* ================================================================
   HELPERS
   ================================================================ */

const safeNumber = (val, decimals = 2, fallback = '—') => {
  if (val === null || val === undefined || isNaN(val) || !isFinite(val)) {
    return fallback;
  }
  return Number(val).toFixed(decimals);
};

const safeRound = (val, fallback = '—') => {
  if (val === null || val === undefined || isNaN(val) || !isFinite(val)) {
    return fallback;
  }
  return Math.round(Number(val));
};

const INDEX_TABS = [
  { id: 'NIFTY', label: 'NIFTY 50' },
  { id: 'BANKNIFTY', label: 'BANKNIFTY' },
  { id: 'FINNIFTY', label: 'FINNIFTY' },
  { id: 'SENSEX', label: 'SENSEX' },
];

const DEFAULT_INDEX_META = {
  NIFTY: 'Nifty 50',
  SENSEX: 'BSE Sensex',
  BANKNIFTY: 'Bank Nifty',
  FINNIFTY: 'Fin Nifty',
};

const changeColor = (v, theme) =>
  v > 0 ? theme.up : v < 0 ? theme.down : theme.textSoft;

const changeArrow = (v) => (v > 0 ? '▲' : v < 0 ? '▼' : '≈');

const getMomentumMeta = (v = 0, theme) => {
  if (v > 0.3) return { icon: '▲', color: theme.up };
  if (v < -0.3) return { icon: '▼', color: theme.down };
  return { icon: '•', color: theme.textFaint };
};

const getPCRColors = (level, theme) => {
  const map = {
    very_bearish: { bg: theme.isDark ? 'rgba(52,211,153,0.16)' : 'rgba(76,175,80,0.14)', text: theme.up },
    bearish: { bg: theme.isDark ? 'rgba(52,211,153,0.10)' : 'rgba(139,195,74,0.12)', text: theme.up },
    neutral: { bg: theme.isDark ? 'rgba(250,204,21,0.12)' : 'rgba(255,193,7,0.12)', text: '#d97706' },
    bullish: { bg: theme.isDark ? 'rgba(248,113,113,0.10)' : 'rgba(255,152,0,0.12)', text: '#ea580c' },
    very_bullish: { bg: theme.isDark ? 'rgba(248,113,113,0.18)' : 'rgba(244,67,54,0.14)', text: theme.down },
  };
  return map[level] || map.neutral;
};

// Same "has this level been touched by today's range" logic used on desktop
// (AnalyticsPage.jsx / TopBuildersTile). Determines the ⚪ vs 🟢/🔴 icon.
const hasStrikeBeenTouched = (strike, ohlc) => {
  if (!ohlc || !strike) return false;
  const high = ohlc.high || 0;
  const low = ohlc.low || 0;
  return strike >= low && strike <= high;
};

/* ================================================================
   THEME
   ================================================================ */

const THEMES = {
  light: {
    isDark: false,
    bg: 'linear-gradient(180deg, #f4f6fb 0%, #eef1f8 100%)',
    card: '#ffffff',
    cardAlt: '#f7f8fc',
    border: '#e6e9f2',
    text: '#1a1d29',
    textSoft: '#5b6072',
    textFaint: '#9aa1b1',
    shadow: '0 2px 10px rgba(30,41,59,0.06)',
    accent: '#2563eb',
    accentSoft: 'rgba(37,99,235,0.10)',
    up: '#16a34a',
    down: '#dc2626',
    tabBg: '#eef1f8',
  },
  dark: {
    isDark: true,
    bg: 'linear-gradient(180deg, #0f1117 0%, #161925 100%)',
    card: '#1a1d29',
    cardAlt: '#20232f',
    border: '#2a2e3d',
    text: '#f1f2f6',
    textSoft: '#a3a8b8',
    textFaint: '#6b7280',
    shadow: '0 2px 14px rgba(0,0,0,0.35)',
    accent: '#5b8def',
    accentSoft: 'rgba(91,141,239,0.16)',
    up: '#34d399',
    down: '#f87171',
    tabBg: '#20232f',
  },
};

const ScrollbarHideStyle = () => (
  <style>{`
    .eg-hscroll::-webkit-scrollbar { display: none; }
    .eg-hscroll { -ms-overflow-style: none; scrollbar-width: none; }
  `}</style>
);

const iconBtnStyle = (theme) => ({
  flexShrink: 0,
  width: '34px',
  height: '34px',
  borderRadius: '9px',
  border: `1px solid ${theme.border}`,
  background: theme.tabBg,
  color: theme.text,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
});

const SectionLabel = ({ theme, text, right }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '8px',
    }}
  >
    <div
      style={{
        fontSize: '0.7rem',
        fontWeight: 800,
        color: theme.textSoft,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
      }}
    >
      {text}
    </div>
    {right}
  </div>
);

/* ================================================================
   HEADER — index tabs + settings + theme toggle
   ================================================================ */

const MobileHeader = ({ theme, activeSymbol, onSelect, onSettings, onToggleTheme,onShare, isDark }) => (
  <div
    style={{
      position: 'sticky',
      top: 0,
      zIndex: 50,
      background: theme.card,
      borderBottom: `1px solid ${theme.border}`,
      padding: '10px 12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    }}
  >

    <img
      src={isDark ? "/icon-192-dark.png" : "/icon-192-light.png"}
      alt="EasyGreeks"
      style={{
        width: "34px",
        height: "34px",
        objectFit: "contain",
        flexShrink: 0,
      }}
    />

    <div className="eg-hscroll" style={{ display: 'flex', gap: '8px', overflowX: 'auto', flex: 1 }}>
      {INDEX_TABS.map((tab) => {
        const isActive = tab.id === activeSymbol;
        return (
          <button
            key={tab.id}
            onClick={() => onSelect(tab.id)}
            style={{
              flexShrink: 0,
              padding: '9px 16px',
              borderRadius: '10px',
              border: `1.5px solid ${isActive ? theme.accent : 'transparent'}`,
              background: isActive ? theme.accent : theme.tabBg,
              color: isActive ? '#fff' : theme.text,
              fontSize: '0.78rem',
              fontWeight: 800,
              letterSpacing: '0.2px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
            }}
          >
            {tab.label}
            {isActive && (
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', flexShrink: 0 }} />
            )}
          </button>
        );
      })}
    </div>

    {/* Share Analytics */}
    <button
      onClick={onShare}
      style={iconBtnStyle(theme)}
      title="Share Analytics"
      aria-label="Share Analytics"
    >
      <Share2 size={18} strokeWidth={2.2} />
    </button>


    <button onClick={onToggleTheme} style={iconBtnStyle(theme)} title="Toggle theme">
      {isDark ? <Sun size={16} /> : <Moon size={16} />}
    </button>
    <button onClick={onSettings} style={iconBtnStyle(theme)} title="Dashboard settings">
      <SlidersHorizontal size={16} />
    </button>
  </div>
);

/* ================================================================
   RATIO CARDS ROW — Bias / Adv-Dec / VIX / Max Pain / Trend / PCR
   (matches the prototype's 6-card top strip)
   ================================================================ */

const RatioCard = ({ label, value, valueColor, footer, theme }) => (
  <div
    style={{
      minWidth: '100px',
      flexShrink: 0,
      padding: '10px 12px',      
      background: theme.card,
      borderRadius: '12px',
      border: `1px solid ${theme.border}`,
      boxShadow: theme.shadow,
    }}
  >
    <div
      style={{
        fontSize: '0.58rem',
        fontWeight: 800,
        color: theme.textFaint,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        marginBottom: '5px',
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: '1rem', fontWeight: 900, color: valueColor || theme.text, marginBottom: footer ? '3px' : 0 }}>
      {value}
    </div>
    {footer}
  </div>
);

const RatioCardsRow = ({ theme, data }) => {
  if (!data || data.status === 'warming_up') return null;

  const { bias_data, pcr_analysis, ratio_metrics, action_leaders } = data;
  const atmPcr = pcr_analysis?.find((p) => p.is_atm);
  const overallPcr = ratio_metrics?.overall_pcr;
  const avgAtmIv = ratio_metrics?.avg_atm_iv;
  const volLeader = action_leaders?.volume;
  const volLeaderColor = volLeader ? (volLeader.side === 'CE' ? theme.up : theme.down) : theme.textFaint;

  const biasColor =
    bias_data?.signal === 'BUY_CALL' ? theme.up : bias_data?.signal === 'BUY_PUT' ? theme.down : theme.text;

  const trendLabel =
    bias_data?.signal === 'BUY_CALL' ? 'BULLISH' : bias_data?.signal === 'BUY_PUT' ? 'BEARISH' : 'NEUTRAL';
  const trendColor =
    bias_data?.signal === 'BUY_CALL' ? theme.up : bias_data?.signal === 'BUY_PUT' ? theme.down : theme.textSoft;
  const regimeLabel = bias_data?.regime ? bias_data.regime.replace(/_/g, ' ') : null;

  const pctFooter = (v) =>
    typeof v === 'number' ? (
      <div style={{ fontSize: '0.64rem', fontWeight: 700, color: changeColor(v, theme) }}>
        {v > 0 ? '▲ +' : v < 0 ? '▼ ' : '≈ '}
        {safeNumber(v, 1)}%
      </div>
    ) : null;

  const placeholderFooter = (
    <div style={{ fontSize: '0.6rem', fontWeight: 700, color: theme.textFaint, fontStyle: 'italic' }}>—</div>
  );

  return (
    <div className="eg-hscroll" style={{ display: 'flex', gap: '8px', overflowX: 'auto', padding: '4px 12px 12px' }}>

      <RatioCard
        theme={theme}
        label="Vol Leader"
        value={volLeader ? `${volLeader.strike} ${volLeader.side}` : '—'}
        valueColor={volLeaderColor}
        footer={
          volLeader ? (
            <div style={{ fontSize: '0.64rem', fontWeight: 700, color: theme.textFaint }}>
              {volLeader.weight_pct}% · {safeNumber(volLeader.duration_min, 1)}m
            </div>
          ) : placeholderFooter
        }
      />

      <RatioCard
        theme={theme}
        label="PCR (±10)"
        value={overallPcr ? overallPcr.value.toFixed(2) : '—'}
        footer={overallPcr ? pctFooter(overallPcr.change_pct) : placeholderFooter}
      />

      <RatioCard
        theme={theme}
        label="ATM IV"
        value={avgAtmIv ? `${avgAtmIv.value.toFixed(1)}%` : '—'}
        footer={avgAtmIv ? pctFooter(avgAtmIv.change_pct) : placeholderFooter}
      />


      <RatioCard
        theme={theme}
        label="Bias"
        value={safeNumber(bias_data?.bias, 3)}
        valueColor={biasColor}
        footer={pctFooter(bias_data?.change_pct)}
      />

      <RatioCard
        theme={theme}
        label="PCR (ATM)"
        value={atmPcr ? atmPcr.pcr_value.toFixed(2) : '—'}
        footer={atmPcr ? pctFooter(atmPcr.change_pct) : placeholderFooter}
      />

      <RatioCard
        theme={theme}
        label="Trend"
        value={trendLabel}
        valueColor={trendColor}
        footer={
          regimeLabel ? (
            <div style={{ fontSize: '0.6rem', fontWeight: 700, color: theme.textFaint, textTransform: 'capitalize' }}>
              {regimeLabel}
            </div>
          ) : null
        }
      />


      {/* Placeholders — backend doesn't compute these yet. Swap value/footer
          in for the real field the moment the API sends it. */}
      <RatioCard theme={theme} label="Adv/Dec" value="—" valueColor={theme.textFaint} footer={placeholderFooter} />
      <RatioCard theme={theme} label="VIX" value="—" valueColor={theme.textFaint} footer={placeholderFooter} />
      <RatioCard theme={theme} label="Max Pain" value="—" valueColor={theme.textFaint} footer={placeholderFooter} />




    </div>
  );
};


/* ================================================================
   Helper — compact Indian-style number formatting (K / L / Cr)
   for large OI / Volume figures.
   ================================================================ */

const formatBigNumber = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '—';
  const num = Number(val);
  const abs = Math.abs(num);
  if (abs >= 1e7) return `${(num / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `${(num / 1e5).toFixed(2)}L`;
  if (abs >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return `${Math.round(num)}`;
};


/* ================================================================
   HERO — MERGED UNDERLYING + FUTURES CARD
   One card, one border/shadow/background — internally split 2:1
   (underlying : futures) so it reads as a single unit instead of
   two separate boxes competing for space.
   ================================================================ */

const UnderlyingFuturesHeroCard = ({
  theme,
  symbol,
  displayName,
  expiry,
  directionPredictor,
  liveLtp,
  spotChangePct,
  obOs,
  biasData,
  ohlc,
  futuresData,
  futuresExpiry,
}) => {
  const roundedLtp = safeRound(liveLtp);

  const hasPredictor = !!directionPredictor;
  const predictorColor = hasPredictor
    ? (directionPredictor.direction_arrow === '⬆️' ? theme.up :
       directionPredictor.direction_arrow === '⬇️' ? theme.down : theme.textSoft)
    : theme.textFaint;

  // Signal-strength bars — same progressive small/medium/large bars as
  // the desktop dashboard's renderBars(), just sized for this card.
  const SIGNAL_BAR_HEIGHTS = ['8px', '12px', '16px'];
  const renderSignalBars = (barCount, color) => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-end' }}>
      {SIGNAL_BAR_HEIGHTS.slice(0, barCount).map((h, i) => (
        <div key={i} style={{ width: '5px', height: h, background: color, borderRadius: '1.5px' }} />
      ))}
    </div>
  );
  const renderEmptySignalBars = () => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-end' }}>
      {SIGNAL_BAR_HEIGHTS.map((h, i) => (
        <div
          key={i}
          style={{
            width: '5px',
            height: h,
            background: theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
            borderRadius: '1.5px',
          }}
        />
      ))}
    </div>
  );

  const biasColor = biasData?.signal === 'BUY_CALL' ? theme.up : biasData?.signal === 'BUY_PUT' ? theme.down : theme.textSoft;
  const biasIcon = biasData?.signal === 'BUY_CALL' ? '🟢' : biasData?.signal === 'BUY_PUT' ? '🔴' : '⚫';
  const obIsOB = !!obOs && obOs.startsWith('OB');

  const ohlcCells = [
    { label: 'PREV CLOSE', val: ohlc?.close },
    { label: 'DAY OPEN', val: ohlc?.open },
    { label: 'DAY HIGH', val: ohlc?.high },
    { label: 'DAY LOW', val: ohlc?.low },
  ];

  const dividerGrad = theme.isDark
    ? 'linear-gradient(180deg, rgba(91,141,239,0.5), rgba(255,152,0,0.5))'
    : 'linear-gradient(180deg, rgba(37,99,235,0.35), rgba(255,152,0,0.4))';

    // ---- futures side derived values ----
    const hasFutures = !!futuresData;
    const futLtp = hasFutures ? safeRound(futuresData.ltp) : null;
    const futPriceChg = futuresData?.price_change_pct || 0;
    const futOiChg = futuresData?.oi_change_pct || 0;
    const futVolChg = futuresData?.volume_change_pct;
    const futOiRaw = futuresData?.oi;
    const futVolumeRaw = futuresData?.volume;
    const futLabelColor = theme.isDark ? 'rgba(255,255,255,0.6)' : '#8a4b00';
    const futValueColor = theme.isDark ? '#fff' : '#4a2c00';
    const futDividerColor = theme.isDark ? 'rgba(255,255,255,0.15)' : 'rgba(230,81,0,0.25)';

    const futOhlcCells = [
        { label: 'PREV CLOSE', val: futuresData?.ohlc?.close },
        { label: 'DAY OPEN', val: futuresData?.ohlc?.open },
        { label: 'DAY HIGH', val: futuresData?.ohlc?.high },
        { label: 'DAY LOW', val: futuresData?.ohlc?.low },
    ];
    const hasFutOhlc = futOhlcCells.some((c) => c.val !== undefined && c.val !== null);

    return (
        <div
        style={{
            margin: '0 12px 14px',
            display: 'flex',
            alignItems: 'stretch',
            borderRadius: '16px',
            border: `1.5px solid ${theme.accent}`,
            boxShadow: theme.shadow,
            overflow: 'hidden',
        }}
        >

      {/* ---------------- UNDERLYING (2/3) ---------------- */}
      <div
        style={{
          flex: '2 1 0%',
          minWidth: 0,
          background: theme.isDark
            ? 'linear-gradient(160deg, #16233d, #101a2e)'
            : 'linear-gradient(160deg, #eaf2ff, #dbeafe)',
          padding: '14px',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 900, color: theme.accent, letterSpacing: '0.2px' }}>{displayName}</div>
          {expiry && <div style={{ fontSize: '0.58rem', fontWeight: 700, color: theme.textFaint }}>{expiry}</div>}
        </div>

        {/* Direction predictor — same DOM shape whether a signal exists
            or not, so the card never resizes between states */}
        <div
          style={{
            padding: '8px 10px',
            borderRadius: '10px',
            marginBottom: '10px',
            background: hasPredictor
              ? (theme.isDark ? 'rgba(91,141,239,0.14)' : 'rgba(37,99,235,0.10)')
              : (theme.isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
            border: `1px solid ${hasPredictor ? `${predictorColor}66` : theme.border}`,
          }}
        >
          <div style={{ fontSize: '0.52rem', fontWeight: 800, color: theme.textFaint, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '6px' }}>
            Direction Predictor
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '11px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: hasPredictor ? `${predictorColor}22` : (theme.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'),
              }}
            >
              <span style={{ fontSize: hasPredictor ? '1.7rem' : '1.2rem', lineHeight: 1, opacity: hasPredictor ? 1 : 0.4 }}>
                {hasPredictor ? directionPredictor.direction_arrow : '–'}
              </span>
            </div>
            <span style={{ fontSize: '1.55rem', fontWeight: 900, color: predictorColor, lineHeight: 1 }}>
              {hasPredictor ? directionPredictor.strike : '—'}
            </span>
            <div style={{ marginLeft: 'auto', display: 'inline-flex', flexDirection: 'row', alignItems: 'flex-end', gap: '10px',flexShrink: 0,whiteSpace: 'nowrap'}}>
              {hasPredictor ? renderSignalBars(directionPredictor.bar_count, predictorColor) : renderEmptySignalBars()}
              <span style={{ fontSize: '0.65rem', fontWeight: 700, color: theme.textFaint, whiteSpace: 'nowrap' }}>
                {hasPredictor ? `${directionPredictor.duration_min}m` : '--'}
              </span>
            </div>
          </div>
        </div>

        {/* Spot LTP */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '10px' }}>
          <span style={{ fontSize: '1.7rem', fontWeight: 900, color: theme.text }}>{roundedLtp}</span>
          {typeof spotChangePct === 'number' && (
            <span style={{ fontSize: '0.78rem', fontWeight: 800, color: changeColor(spotChangePct, theme) }}>
              {changeArrow(spotChangePct)} {spotChangePct > 0 ? '+' : ''}
              {safeNumber(spotChangePct)}%
            </span>
          )}
        </div>

        {/* OB/OS + Bias */}
        {(obOs || (biasData && typeof biasData.bias === 'number')) && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
            {obOs && (
              <span
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 800,
                  padding: '3px 8px',
                  borderRadius: '6px',
                  background: obIsOB ? 'rgba(255,23,68,0.14)' : 'rgba(0,200,83,0.14)',
                  color: obIsOB ? '#d50000' : '#00895a',
                }}
              >
                {obIsOB ? '🔥' : '❄️'} {obOs}
              </span>
            )}
            {biasData && typeof biasData.bias === 'number' && (
              <span
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 800,
                  padding: '3px 8px',
                  borderRadius: '6px',
                  background: theme.accentSoft,
                  color: biasColor,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                {biasIcon} Bias {safeNumber(biasData.bias, 2)}
                {typeof biasData.change_pct === 'number' && (
                  <span style={{ color: changeColor(biasData.change_pct, theme) }}>
                    {biasData.change_pct > 0 ? '+' : ''}
                    {safeNumber(biasData.change_pct, 1)}%
                  </span>
                )}
              </span>
            )}
          </div>
        )}

        {/* OHLC */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '6px',
            paddingTop: '9px',
            borderTop: `1px dashed ${theme.isDark ? 'rgba(255,255,255,0.15)' : theme.border}`,
          }}
        >
          {ohlcCells.map(({ label, val }) => (
            <div key={label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.5rem', fontWeight: 800, color: theme.textFaint, marginBottom: '2px' }}>{label}</div>
              <div style={{ fontSize: '0.72rem', fontWeight: 900, color: theme.text }}>{safeRound(val)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ---------------- divider ---------------- */}
      <div style={{ width: '1.5px', background: dividerGrad, flexShrink: 0 }} />

      {/* ---------------- FUTURES (1/3) ----------------
          Top block (header/LTP/OI-VOL) and bottom block (OHLC-style
          grid) are split into two groups, with the column using
          justify-content: space-between — so the bottom block always
          sits flush at the same baseline as the underlying's OHLC
          row, regardless of how much content is above it. */}
      <div
        style={{
          flex: '1 1 0%',
          minWidth: 0,
          background: theme.isDark
            ? 'linear-gradient(160deg, #3a2a12, #241705)'
            : 'linear-gradient(160deg, #fff3e0, #ffe0b2)',
          padding: '12px 10px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        {!hasFutures ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
              <span style={{ fontSize: '0.62rem', fontWeight: 900, color: '#e65100', textTransform: 'uppercase' }}>📊 FUT</span>
              {futuresExpiry && <span style={{ fontSize: '0.5rem', fontWeight: 700, color: futLabelColor }}>{futuresExpiry}</span>}
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <span style={{ fontSize: '0.6rem', color: theme.textFaint }}>No futures data</span>
            </div>
          </>
        ) : (
          <>
            {/* ---- top group: header + LTP + OI/VOL ---- */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
                <span style={{ fontSize: '0.62rem', fontWeight: 900, color: '#e65100', textTransform: 'uppercase' }}>📊 FUT</span>
                {futuresExpiry && <span style={{ fontSize: '0.5rem', fontWeight: 700, color: futLabelColor }}>{futuresExpiry}</span>}
              </div>

              <div style={{ textAlign: 'center', marginBottom: '9px' }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 900, color: futValueColor, lineHeight: 1.1 }}>₹{futLtp}</div>
                <div style={{ fontSize: '0.64rem', fontWeight: 800, color: changeColor(futPriceChg, theme) }}>
                  {changeArrow(futPriceChg)} {futPriceChg > 0 ? '+' : ''}
                  {safeNumber(futPriceChg, 1)}%
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '6px',
                  paddingTop: '8px',
                  borderTop: `1px dashed ${futDividerColor}`,
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.5rem', fontWeight: 800, color: futLabelColor, marginBottom: '2px' }}>OI</div>
                  <div style={{ fontSize: '0.74rem', fontWeight: 900, color: futValueColor, marginBottom: '2px' }}>
                    {formatBigNumber(futOiRaw)}
                  </div>
                  <div style={{ fontSize: '0.58rem', fontWeight: 800, color: changeColor(futOiChg, theme) }}>
                    {changeArrow(futOiChg)} {futOiChg > 0 ? '+' : ''}
                    {safeNumber(futOiChg, 1)}%
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.5rem', fontWeight: 800, color: futLabelColor, marginBottom: '2px' }}>VOL</div>
                  <div style={{ fontSize: '0.74rem', fontWeight: 900, color: futValueColor, marginBottom: '2px' }}>
                    {formatBigNumber(futVolumeRaw)}
                  </div>
                  <div style={{ fontSize: '0.58rem', fontWeight: 800, color: typeof futVolChg === 'number' ? changeColor(futVolChg, theme) : theme.textFaint }}>
                    {typeof futVolChg === 'number' ? `${changeArrow(futVolChg)} ${futVolChg > 0 ? '+' : ''}${safeNumber(futVolChg, 1)}%` : '—'}
                  </div>
                </div>
              </div>
            </div>

            {/* ---- bottom group: Prev Close/Day Open + Day High/Day Low,
                 pinned to the container's bottom edge via
                 justify-content: space-between on the parent ---- */}
            {hasFutOhlc && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px 6px',
                  paddingTop: '8px',
                  borderTop: `1px dashed ${futDividerColor}`,
                }}
              >
                {futOhlcCells.map(({ label, val }) => (
                  <div key={label} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.5rem', fontWeight: 800, color: futLabelColor, marginBottom: '2px' }}>{label}</div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 900, color: futValueColor }}>{safeRound(val)}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};


/* ================================================================
   S / R LEVELS ROW — unchanged
   ================================================================ */

const SR_LEVEL_CONFIG = [
  { key: 's_ext', label: 'S-Ext', color: '#ff5252' },
  { key: 'support', label: 'Support', color: '#00c853' },
  { key: 'resistance', label: 'Resistance', color: '#d32f2f' },
  { key: 'r_ext', label: 'R-Ext', color: '#ff1744' },
];

const CATEGORY_ORDER = ['Vol', 'OI', 'ΔW OI', 'OI×Vega', 'OI×Gamma'];
const CATEGORY_SHORT_LABELS = { 'Vol': 'Vol', 'OI': 'OI', 'ΔW OI': 'OIΔ', 'OI×Vega': 'OIV', 'OI×Gamma': 'OIG' };

const SRLevelCard = ({ theme, label, color, level }) => {
  const strike = level?.strike;
  const side = level?.side;
  const displayStrike = strike ? `${strike} ${side || ''}`.trim() : '—';
  const strengthPct = level?.strength_pct || 0;
  const crownPcts = level?.crown_pcts;
  const normPcts = level?.norm_pcts;
  const hasBreakdown = crownPcts && Object.keys(crownPcts).length > 0;

  return (
    <div
      style={{
        flexShrink: 0,
        minWidth: '150px',
        padding: '10px 12px',
        borderRadius: '12px',
        background: theme.isDark ? `${color}1c` : `${color}0d`,
        border: `1.5px solid ${color}`,
        boxShadow: theme.shadow,
      }}
    >
      <div style={{ fontSize: '0.58rem', fontWeight: 800, color, textTransform: 'uppercase', marginBottom: '4px', textAlign: 'center' }}>
        {label}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '6px' }}>
        {level?.arrow && <span style={{ fontSize: '0.8rem', color }}>{level.arrow}</span>}
        <span style={{ fontSize: '0.95rem', fontWeight: 900, color: theme.text }}>{displayStrike}</span>
      </div>

      {strengthPct > 0 && (
        <div style={{ marginBottom: '7px' }}>
          <div
            style={{
              width: '100%',
              height: '4px',
              background: theme.isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
              borderRadius: '2px',
              overflow: 'hidden',
            }}
          >
            <div style={{ width: `${strengthPct}%`, height: '100%', background: color }} />
          </div>
          <div style={{ fontSize: '0.6rem', fontWeight: 800, color, textAlign: 'center', marginTop: '3px' }}>
            {strengthPct}%
            {level.remaining_indicator && (
              <span
                style={{
                  marginLeft: '5px',
                  fontSize: '0.5rem',
                  fontWeight: 900,
                  color: '#ff9800',
                  background: 'rgba(255,152,0,0.15)',
                  padding: '1px 4px',
                  borderRadius: '3px',
                }}
              >
                {level.remaining_indicator}
              </span>
            )}
          </div>
        </div>
      )}

      {hasBreakdown && (
        <div style={{ fontSize: '0.55rem', paddingTop: '5px', borderTop: `1px dashed ${theme.border}` }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '2px',
              paddingBottom: '2px',
              borderBottom: `1px solid ${theme.border}`,
            }}
          >
            {CATEGORY_ORDER.map((cat) => (
              <span key={cat} style={{ flex: 1, textAlign: 'center', fontWeight: 800, color: theme.textFaint }}>
                {CATEGORY_SHORT_LABELS[cat]}
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1px' }}>
            {CATEGORY_ORDER.map((cat) => {
              const pct = crownPcts[cat] || 0;
              const c = pct >= 90 ? '#00c853' : pct >= 60 ? '#ff9800' : '#f44336';
              return (
                <span key={cat} style={{ flex: 1, textAlign: 'center', fontSize: '0.72rem',fontWeight: 800, color: c }}>
                  {pct}
                </span>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {CATEGORY_ORDER.map((cat) => {
              const pct = normPcts?.[cat] || 0;
              const c = pct >= 90 ? '#4fc3f7' : theme.textFaint;
              return (
                <span key={cat} style={{ flex: 1, textAlign: 'center', fontWeight: 700, color: c, opacity: 0.85 }}>
                  {pct}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const SRLevelsRow = ({ theme, symbol, srLevels }) => {
  if (!srLevels) return null;
  return (
    <div style={{ margin: '0 12px 14px' }}>
      <SectionLabel theme={theme} text={`${symbol} · Support / Resistance`} />
      <div className="eg-hscroll" style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
        {SR_LEVEL_CONFIG.map(({ key, label, color }) => (
          <SRLevelCard key={key} theme={theme} label={label} color={color} level={srLevels[key]} />
        ))}
      </div>
    </div>
  );
};

/* ================================================================
   HERO ROW — renders the merged card, then S/R levels underneath.
   ================================================================ */

const UnderlyingFuturesRow = ({ theme, symbol, displayName, data, liveSymbolData, loading }) => {
  if (!data) {
    return (
      <div
        style={{
          margin: '0 12px 14px',
          padding: '24px',
          textAlign: 'center',
          color: theme.textFaint,
          background: theme.card,
          borderRadius: '14px',
          border: `1px solid ${theme.border}`,
        }}
      >
        {loading ? '⏳ Loading market data…' : 'No data yet'}
      </div>
    );
  }

  if (data.status === 'warming_up') {
    return (
      <div
        style={{
          margin: '0 12px 14px',
          padding: '24px',
          textAlign: 'center',
          color: theme.textFaint,
          background: theme.card,
          borderRadius: '14px',
          border: `1px solid ${theme.border}`,
        }}
      >
        ⏳ Warming up…
      </div>
    );
  }

  const { sr_levels, spot_price, spot_change_pct, ob_os, bias_data, futures_data, expiry_display, direction_predictor } = data;

  const liveLtp = liveSymbolData?.spot?.ltp ?? spot_price;
  const ohlc = liveSymbolData?.spot?.ohlc || {};

  const liveFuturesData = liveSymbolData?.futures?.ltp
    ? { ...futures_data, ltp: liveSymbolData.futures.ltp, ohlc: liveSymbolData.futures.ohlc || futures_data?.ohlc }
    : futures_data;

  return (
    <div>
      <UnderlyingFuturesHeroCard
        theme={theme}
        symbol={symbol}
        displayName={displayName}
        expiry={expiry_display}
        directionPredictor={direction_predictor}
        liveLtp={liveLtp}
        spotChangePct={spot_change_pct}
        obOs={ob_os}
        biasData={bias_data}
        ohlc={ohlc}
        futuresData={liveFuturesData}
        futuresExpiry={futures_data?.expiry || expiry_display}
      />

      <SRLevelsRow theme={theme} symbol={symbol} srLevels={sr_levels} />
    </div>
  );
};

/* ================================================================
   OPTION LTP ROW  (OTM / ATM / ITM)
   Day-high / day-low now show on hover/tap, mirroring the desktop
   OptionLTPTile tooltip behaviour exactly (same trigger condition,
   same fields).
   ================================================================ */

const OptionLTPRow = ({ theme, optionsData }) => {
  const [showTooltip, setShowTooltip] = useState({});

  const isEmpty = !optionsData || Object.values(optionsData).every((v) => !v || Object.keys(v).length === 0);
  if (isEmpty) return null;

  const buckets = { OTM: {}, ATM: {}, ITM: {} };
  buckets.ATM = optionsData['ATM'] || {};
  if (optionsData['ATM-1']?.CE) buckets.ITM.CE = optionsData['ATM-1'].CE;
  if (optionsData['ATM+1']?.PE) buckets.ITM.PE = optionsData['ATM+1'].PE;
  if (optionsData['ATM+1']?.CE) buckets.OTM.CE = optionsData['ATM+1'].CE;
  if (optionsData['ATM-1']?.PE) buckets.OTM.PE = optionsData['ATM-1'].PE;

  const order = ['OTM', 'ATM', 'ITM'];
  const headerColor = { OTM: '#c2185b', ATM: '#ef6c00', ITM: '#2e7d32' };
  const headerBg = {
    OTM: theme.isDark ? 'rgba(236,72,153,0.14)' : '#fce4ec',
    ATM: theme.isDark ? 'rgba(251,146,60,0.16)' : '#fff3e0',
    ITM: theme.isDark ? 'rgba(52,211,153,0.14)' : '#e8f5e9',
  };

  const setTip = (key, val) => setShowTooltip((prev) => ({ ...prev, [key]: val }));

  return (
    <div style={{ margin: '0 12px 14px' }}>
      <SectionLabel theme={theme} text="Option LTP" />
      <div style={{ display: 'flex', gap: '8px' }}>
        {order.map((label) => {
          const bucket = buckets[label];
          if (!bucket || Object.keys(bucket).length === 0) return <div key={label} style={{ flex: 1 }} />;
          const isAtm = label === 'ATM';
          return (
            <div
              key={label}
              style={{
                flex: 1,
                minWidth: 0,
                background: theme.card,
                borderRadius: '12px',
                border: isAtm ? `2px solid ${headerColor[label]}` : `1px solid ${theme.border}`,
                boxShadow: theme.shadow,
                padding: '8px',
              }}
            >
              <div
                style={{
                  textAlign: 'center',
                  fontSize: '0.6rem',
                  fontWeight: 800,
                  color: headerColor[label],
                  background: headerBg[label],
                  borderRadius: '6px',
                  padding: '3px 0',
                  marginBottom: '6px',
                }}
              >
                {label}
              </div>
              {['CE', 'PE'].map((type) => {
                const opt = bucket[type];
                if (!opt) return null;
                const col = type === 'CE' ? '#2e7d32' : '#c62828';
                const ohlc = opt.ohlc || {};
                const hasOHLC = ohlc.high && ohlc.low;
                const tooltipKey = `${label}_${type}`;

                return (
                  <div
                    key={type}
                    style={{
                      padding: '5px 4px',
                      marginBottom: '4px',
                      borderRadius: '6px',
                      background: type === 'CE' ? (theme.isDark ? 'rgba(52,211,153,0.10)' : 'rgba(76,175,80,0.08)') : theme.isDark ? 'rgba(248,113,113,0.10)' : 'rgba(244,67,54,0.08)',
                      border: `1px solid ${col}55`,
                      textAlign: 'center',
                      position: 'relative',
                      cursor: hasOHLC ? 'pointer' : 'default',
                    }}
                    onMouseEnter={() => hasOHLC && setTip(tooltipKey, true)}
                    onMouseLeave={() => setTip(tooltipKey, false)}
                    onClick={() => hasOHLC && setTip(tooltipKey, !showTooltip[tooltipKey])}
                  >
                    <div style={{ fontSize: '0.53rem', fontWeight: 800, color: col }}>
                      {opt.strike} {type}
                    </div>
                    <div style={{ fontSize: '0.76rem', fontWeight: 900, color: theme.text }}>₹{safeNumber(opt.ltp)}</div>
                    <div style={{ fontSize: '0.53rem', fontWeight: 700, color: changeColor(opt.change_pct_avg || 0, theme) }}>
                      {(opt.change_pct_avg || 0) > 0 ? '+' : ''}
                      {safeNumber(opt.change_pct_avg || 0, 1)}%
                    </div>

                    {/* Day High/Low tooltip — hover on desktop, tap on mobile */}
                    {showTooltip[tooltipKey] && hasOHLC && (
                      <div
                        style={{
                          position: 'absolute',
                          background: 'rgba(0,0,0,0.9)',
                          color: '#fff',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          zIndex: 1000,
                          top: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          marginTop: '6px',
                          pointerEvents: 'none',
                        }}
                      >
                        <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px', textAlign: 'left' }}>
                          <span>{label} {type} ({opt.strike})</span>
                          <span></span>
                          <span>Day High:</span>
                          <span style={{ color: '#4CAF50', fontWeight: 800 }}>₹{safeNumber(ohlc.high)}</span>
                          <span>Day Low:</span>
                          <span style={{ color: '#f44336', fontWeight: 800 }}>₹{safeNumber(ohlc.low)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ================================================================
   PCR ROW
   ================================================================ */
 
const PCRRow = ({ theme, pcrData, symbol }) => {
  const scrollRef = useRef(null);
  // Tracks which symbol's strip we've already auto-centered, so refetches
  // of the SAME symbol (every few seconds) don't keep yanking the scroll
  // position away from wherever the user has scrolled to. A symbol change
  // (tab switch) is treated as a fresh view and re-centers once.
  const centeredForRef = useRef(null);
 
  useEffect(() => {
    if (!pcrData || pcrData.length === 0) return;
    if (centeredForRef.current === symbol) return;
    const container = scrollRef.current;
    if (!container) return;
 
    const atmIndex = pcrData.findIndex((p) => p.is_atm);
    const targetIndex = atmIndex !== -1 ? atmIndex : Math.floor(pcrData.length / 2);
 
    // Wait a frame so the cards have actually been laid out before we
    // measure offsetLeft/offsetWidth.
    requestAnimationFrame(() => {
      const cardEl = container.children[targetIndex];
      if (cardEl) {
        container.scrollLeft = cardEl.offsetLeft - container.clientWidth / 2 + cardEl.offsetWidth / 2;
      }
      centeredForRef.current = symbol;
    });
  }, [pcrData, symbol]);
 
  if (!pcrData || pcrData.length === 0) return null;
  return (
    <div style={{ margin: '0 12px 14px' }}>
      <SectionLabel theme={theme} text="PCR (ATM ±5)" />
      <div ref={scrollRef} className="eg-hscroll" style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
        {pcrData.map((item, idx) => {
          const c = getPCRColors(item.pcr_level, theme);
          return (
            <div
              key={idx}
              style={{
                flexShrink: 0,
                minWidth: '76px',
                padding: '8px 6px',
                borderRadius: '10px',
                background: item.is_atm ? theme.accentSoft : c.bg,
                border: `${item.is_atm ? 2 : 1}px solid ${item.is_atm ? theme.accent : c.text + '33'}`,
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '0.56rem', fontWeight: item.is_atm ? 900 : 700, color: item.is_atm ? theme.accent : theme.textFaint, marginBottom: '2px' }}>
                {item.atm_label}
              </div>
              <div style={{ fontSize: '0.68rem', fontWeight: 800, color: theme.text, marginBottom: '4px' }}>{item.strike}</div>
              <div style={{ fontSize: '0.82rem', fontWeight: 900, color: c.text }}>{item.pcr_value.toFixed(2)}</div>
              {typeof item.change_pct === 'number' && (
                <div style={{ fontSize: '0.58rem', fontWeight: 700, color: changeColor(item.change_pct, theme) }}>
                  {item.change_pct > 0 ? '+' : ''}
                  {item.change_pct.toFixed(1)}%{item.strong_change ? ' 🔥' : item.crosses_threshold ? ' ⚡' : ''}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
 


/* ================================================================
   TOP BUILDERS & REGIME ANALYSIS
   Rebuilt as an actual Category × Top1-5 table, matching the
   prototype, wrapped in a horizontally-scrolling container with a
   sticky Category column (real phones are ~380-420px wide, so the
   full 5-column table can't fit without scrolling the way it does
   in the wide prototype mock).
   Icon logic (🟢/🔴/⚪) is copied 1:1 from the desktop
   TopBuildersTile (AnalyticsPage.jsx) so both views agree.
   ================================================================ */

// Order matches the prototype rows: Vol, Delta OI, Gamma OI, Vega OI, OI
const CATEGORY_META = [
  { key: 'vol_builders', regimeKey: 'volume', label: 'Vol', icon: '📊' },
  { key: 'oi_delta_builders', regimeKey: 'oi_delta', label: 'Delta OI', icon: '⚡' },
  { key: 'oi_gamma_builders', regimeKey: 'oi_gamma', label: 'Gamma OI', icon: '🎯' },
  { key: 'oi_vega_builders', regimeKey: 'oi_vega', label: 'Vega OI', icon: '💎' },
  { key: 'oi_builders', regimeKey: 'oi', label: 'OI', icon: '🔥' },
];

const BuilderCell = ({ b, theme, touched, highlighted, showDurationRank }) => {
  if (!b) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textFaint, fontSize: '0.62rem' }}>
        –
      </div>
    );
  }

  const { icon, color } = getMomentumMeta(b.rel_momentum, theme);
  const barColor = b.side === 'CE' ? '#f44336' : '#4CAF50';
  const strikeIcon = touched ? '⚪' : b.side === 'CE' ? '🟢' : '🔴';
  const prevRank = b.prev_ranks?.length ? b.prev_ranks[0] : null;

  return (
    <div
      style={{
        padding: '7px 5px',
        borderRadius: '8px',
        background: highlighted ? (theme.isDark ? 'rgba(250,204,21,0.16)' : 'rgba(255,215,0,0.20)') : theme.cardAlt,
        border: highlighted ? '1.5px solid #eab308' : `1px solid ${theme.border}`,
        textAlign: 'center',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2px',
      }}
    >
      <div style={{ fontSize: '0.6rem', fontWeight: 800, color: theme.text, whiteSpace: 'nowrap' }}>
        {strikeIcon} {b.strike} {b.side}
      </div>
      <div style={{ fontSize: '0.62rem', fontWeight: 900, color: barColor }}>
        {b.relative_pct ?? 0}%
      </div>
      <div style={{ fontSize: '0.5rem', fontWeight: 700, color, whiteSpace: 'nowrap' }}>
        {icon}
        {Math.abs(b.rel_momentum || 0).toFixed(1)}
        {showDurationRank && b.duration_minutes > 0 ? ` ${b.duration_minutes}m` : ''}
        {showDurationRank && prevRank ? ` #${prevRank}` : ''}
      </div>
    </div>
  );
};

const RegimeDetailRow = ({ theme, label, icon, regimeDays }) => {
  const d = regimeDays?.intraday;
  const hasAny = d?.rank_change || d?.expansion || regimeDays?.day_minus_1 || regimeDays?.day_minus_2 || regimeDays?.day_minus_3;
  if (!hasAny) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 10px',
        borderRadius: '8px',
        background: theme.cardAlt,
        border: `1px solid ${theme.border}`,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: '0.66rem', fontWeight: 800, color: theme.text, minWidth: '72px' }}>
        {icon} {label}
      </span>
      {d?.rank_change && (
        <span style={{ fontSize: '0.6rem', color: theme.textFaint }}>
          Rank rot: <b style={{ color: d.rank_change === 'UP' ? theme.up : theme.down }}>{d.rank_change === 'UP' ? '▲' : '▼'}</b>
        </span>
      )}
      {d?.expansion && (
        <span style={{ fontSize: '0.6rem', color: theme.textFaint }}>
          Expansion: <b style={{ color: d.expansion === 'UP' ? theme.up : theme.down }}>{d.expansion === 'UP' ? '▲' : '▼'}</b>
        </span>
      )}
      {['day_minus_1', 'day_minus_2', 'day_minus_3'].map((dkey, idx) => {
        const dd = regimeDays?.[dkey];
        if (!dd?.target_strike) return null;
        return (
          <span key={dkey} style={{ fontSize: '0.6rem', color: theme.textFaint }}>
            D-{idx + 1}: <b style={{ color: theme.text }}>{dd.target_strike} {dd.target_side}</b>
          </span>
        );
      })}
    </div>
  );
};

// Compact Trend / Rank-Rotation / Expansion indicator block shown inside
// each category cell — e.g.
//   📊 Vol
//   T   R   E
//   ▲   ▼   ▲
const TrendRankExpansionGrid = ({ theme, trend, rankChange, expansion }) => {
  const arrowFor = (sig) => (sig === 'UP' ? '▲' : sig === 'DOWN' ? '▼' : '–');
  const colorFor = (sig) => (sig === 'UP' ? theme.up : sig === 'DOWN' ? theme.down : theme.textFaint);
  const signals = [trend, rankChange, expansion];

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2px' }}>
        {['T', 'R', 'E'].map((k) => (
          <span key={k} style={{ fontSize: '0.46rem', fontWeight: 800, color: theme.textFaint, textAlign: 'center' }}>
            {k}
          </span>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2px' }}>
        {signals.map((sig, i) => (
          <span key={i} style={{ fontSize: '0.62rem', fontWeight: 900, textAlign: 'center', color: colorFor(sig), lineHeight: 1 }}>
            {arrowFor(sig)}
          </span>
        ))}
      </div>
    </div>
  );
};

const TopBuildersSection = ({ theme, buildersData, regimeAnalysis, netExposures, spotOhlc }) => {
  const [showRegime, setShowRegime] = useState(false);

  if (!buildersData || (!buildersData.oi_builders?.length && !buildersData.vol_builders?.length)) {
    return null;
  }

  const rows = CATEGORY_META.map(({ key, regimeKey, label, icon }) => {
    const builders = (buildersData[key] || []).slice(0, 5);
    if (builders.length === 0) return null;

    // Highlight the strongest momentum builder — same rule as desktop:
    // if everyone but #1 is losing momentum, keep #1 highlighted.
    let highlightIdx = 0;
    let maxMomentum = builders[0]?.rel_momentum ?? -Infinity;
    builders.forEach((b, i) => {
      if ((b.rel_momentum ?? -Infinity) > maxMomentum) {
        maxMomentum = b.rel_momentum;
        highlightIdx = i;
      }
    });
    if (builders.length >= 2) {
      const rest = builders.slice(1);
      if (rest.every((b) => (b.rel_momentum || 0) < 0)) highlightIdx = 0;
    }

    return {
      key,
      label,
      icon,
      builders,
      highlightIdx,
      trend: regimeAnalysis?.[regimeKey]?.intraday?.builder_trend,
      rankChange: regimeAnalysis?.[regimeKey]?.intraday?.rank_change,
      expansion: regimeAnalysis?.[regimeKey]?.intraday?.expansion,
      regimeDays: regimeAnalysis?.[regimeKey],
    };
  }).filter(Boolean);

  if (rows.length === 0) return null;

  

  const catColStyle = {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    background: theme.card,
    minWidth: '65px',
    maxWidth: '65px',
    
  };

  return (
    <div style={{ margin: '0 12px 20px' }}>
      <SectionLabel
        theme={theme}
        text="Top Builders & Regime"
        right={
          <button
            onClick={() => setShowRegime((v) => !v)}
            style={{
              border: 'none',
              background: 'none',
              padding: 0,
              fontSize: '0.62rem',
              fontWeight: 800,
              color: theme.accent,
              cursor: 'pointer',
            }}
          >
            {showRegime ? 'Hide regime ▲' : 'Regime detail ▼'}
          </button>
        }
      />

      {netExposures && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
          <div style={{ flex: 1, padding: '8px 10px', borderRadius: '8px', background: theme.accentSoft, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.6rem', fontWeight: 700, color: theme.accent }}>Net Δ</span>
            <span style={{ fontSize: '0.66rem', fontWeight: 900, color: netExposures.normalized_delta > 0 ? theme.up : theme.down }}>
              {netExposures.normalized_delta > 0 ? '+' : ''}
              {netExposures.normalized_delta}
            </span>
          </div>
          <div style={{ flex: 1, padding: '8px 10px', borderRadius: '8px', background: 'rgba(156,39,176,0.10)', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#9c27b0' }}>Net Γ</span>
            <span style={{ fontSize: '0.66rem', fontWeight: 900, color: netExposures.net_gamma > 0 ? theme.up : theme.down }}>
              {netExposures.net_gamma > 0 ? '+' : ''}
              {netExposures.net_gamma.toLocaleString()}
            </span>
          </div>
        </div>
      )}

      <div
        className="eg-hscroll"
        style={{
          overflowX: 'auto',
          border: `1px solid ${theme.border}`,
          borderRadius: '12px',
          background: theme.card,
          boxShadow: theme.shadow,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '78px repeat(5, 96px)', minWidth: '558px' }}>
          {/* Header row */}
          <div style={{ ...catColStyle, padding: '8px 6px', fontSize: '0.58rem', fontWeight: 800, color: theme.textSoft, borderBottom: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center' }}>
            Category
          </div>
          {[1, 2, 3, 4, 5].map((n) => (
            <div
              key={n}
              style={{
                padding: '8px 6px',
                textAlign: 'center',
                fontSize: '0.6rem',
                fontWeight: 800,
                color: theme.textSoft,
                borderBottom: `1px solid ${theme.border}`,
                borderLeft: `1px solid ${theme.border}`,
              }}
            >
              Top {n}
            </div>
          ))}

          {/* Body rows */}
          {rows.map(({ key, label, icon, builders, highlightIdx, trend, rankChange, expansion }) => (
            <React.Fragment key={key}>
              <div
                style={{
                  ...catColStyle,
                  padding: '8px 4px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  borderBottom: `1px solid ${theme.border}`,
                }}
              >
                <span style={{ fontSize: '0.9rem', lineHeight: 1 }}>{icon}</span>
                <span style={{ fontSize: '0.5rem', fontWeight: 800, color: theme.text, textAlign: 'center', lineHeight: 1.1 }}>{label}</span>
                <TrendRankExpansionGrid theme={theme} trend={trend} rankChange={rankChange} expansion={expansion} />
              </div>
              {[0, 1, 2, 3, 4].map((i) => {
                const b = builders[i];
                return (
                  <div
                    key={i}
                    style={{
                      padding: '5px',
                      borderBottom: `1px solid ${theme.border}`,
                      borderLeft: `1px solid ${theme.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <BuilderCell
                      b={b}
                      theme={theme}
                      touched={!!b && hasStrikeBeenTouched(b.strike, spotOhlc)}
                      highlighted={!!b && i === highlightIdx}
                      showDurationRank={i < 3}
                    />
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div style={{ marginTop: '8px', fontSize: '0.56rem', color: theme.textFaint, textAlign: 'center', lineHeight: 1.5 }}>
        % = relative strength · m = duration at rank (Top 3 only) · #N = previous rank
        <br />
        T = Trend · R = Rank Rotation · E = Expansion (▲ up · ▼ down)
      </div>

      {showRegime && (
        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {rows.map(({ key, label, icon, regimeDays }) => (
            <RegimeDetailRow key={key} theme={theme} label={label} icon={icon} regimeDays={regimeDays} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ================================================================
   SETTINGS SHEET — module visibility (index switching stays in the
   header tabs, so this only manages which rows are visible)
   ================================================================ */

const MobileSettingsSheet = ({ theme, onClose, activeModules, setActiveModules }) => (
  <>
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200 }} />
    <div
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 201,
        background: theme.card,
        borderTopLeftRadius: '18px',
        borderTopRightRadius: '18px',
        padding: '18px',
        boxShadow: '0 -8px 30px rgba(0,0,0,0.25)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <div style={{ fontSize: '1rem', fontWeight: 800, color: theme.text }}>Dashboard Modules</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <X size={20} color={theme.textFaint} />
        </button>
      </div>

      {ANALYTICS_MODULES.map((mod) => {
        const checked = activeModules.includes(mod.id);
        return (
          <div
            key={mod.id}
            onClick={() => setActiveModules((prev) => (checked ? prev.filter((m) => m !== mod.id) : [...prev, mod.id]))}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 14px',
              borderRadius: '10px',
              marginBottom: '8px',
              background: checked ? theme.accentSoft : theme.cardAlt,
              border: `1.5px solid ${checked ? theme.accent : theme.border}`,
              cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: theme.text }}>{mod.label}</span>
            {checked && <Check size={16} color={theme.accent} />}
          </div>
        );
      })}

      <div style={{ marginTop: '10px', fontSize: '0.7rem', color: theme.textFaint, textAlign: 'center' }}>
        Switch indices using the tabs at the top.
      </div>
    </div>
  </>
);

/* ================================================================
   SYMBOL VIEW — composes all rows for the active index
   ================================================================ */

const MobileSymbolView = ({ theme, symbol, displayName, data, loading, lastUpdate, liveSymbolData, optionsData, activeModules }) => {
  // Same source of truth desktop uses for the "has this strike been
  // touched today" (⚪) icon in TopBuildersTile.
  const spotOhlc = liveSymbolData?.spot?.ohlc || data?.futures_data?.ohlc;

  return (
    <div>
      <RatioCardsRow theme={theme} data={data} />
      <UnderlyingFuturesRow theme={theme} symbol={symbol} displayName={displayName} data={data} liveSymbolData={liveSymbolData} loading={loading} />
      {activeModules.includes('optionLTP') && <OptionLTPRow theme={theme} optionsData={optionsData} />}
      {activeModules.includes('pcr') && <PCRRow theme={theme} pcrData={data?.pcr_analysis} />}
      {activeModules.includes('builders') && (
        <TopBuildersSection
          theme={theme}
          buildersData={data?.builders_data}
          regimeAnalysis={data?.regime_analysis}
          netExposures={data?.net_exposures}
          spotOhlc={spotOhlc}
        />
      )}
      {lastUpdate && (
        <div style={{ textAlign: 'center', fontSize: '0.6rem', color: theme.textFaint, marginBottom: '12px' }}>Updated {lastUpdate}</div>
      )}
    </div>
  );
};

/* ================================================================
   ROOT EXPORT
   ================================================================ */

export default function MobileDashboard({
  selectedIndices,
  setSelectedIndices = () => {},
  data,
  loading,
  lastUpdate,
  liveSpot,
  liveOptions,
  indexMetadata,
  activeModules,
  setActiveModules = () => {},
}) {
  const [themeName, setThemeName] = useState('light');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const theme = THEMES[themeName];

  // Self-heal: on the "/compare" (My Dashboard) route, desktop intentionally
  // starts with an empty selection and shows an EmptyState prompting the
  // user to configure it via the sidebar. Mobile has no index-picker in its
  // settings sheet (switching happens via the header tabs), so default to
  // NIFTY instead of rendering a permanently empty screen.
  useEffect(() => {
    if (!selectedIndices || selectedIndices.length === 0) {
      setSelectedIndices(['NIFTY']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeSymbol = (selectedIndices && selectedIndices[0]) || 'NIFTY';

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/${activeSymbol.toLowerCase()}`;

    const shareLabels = {
      NIFTY: 'NIFTY 50',
      BANKNIFTY: 'BANKNIFTY',
      FINNIFTY: 'FINNIFTY',
      SENSEX: 'SENSEX',
    };

    const indexLabel = shareLabels[activeSymbol] || activeSymbol;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `EasyGreeks — ${indexLabel}`,
          url: shareUrl,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Share failed:', error);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('EasyGreeks link copied!');
      } catch (error) {
        console.error('Copy failed:', error);
      }
    }
  };

  const modules = activeModules || [];

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Segoe UI', -apple-system, sans-serif" }}>
      <ScrollbarHideStyle />

      <MobileHeader
        theme={theme}
        activeSymbol={activeSymbol}
        onSelect={(sym) => setSelectedIndices([sym])}
        onSettings={() => setSettingsOpen(true)}
        onToggleTheme={() => setThemeName((t) => (t === 'light' ? 'dark' : 'light'))}
        onShare={handleShare}
        isDark={themeName === 'dark'}
      />

      <div style={{ paddingTop: '12px', paddingBottom: '40px' }}>
        <MobileSymbolView
          theme={theme}
          symbol={activeSymbol}
          displayName={indexMetadata?.[activeSymbol]?.name || DEFAULT_INDEX_META[activeSymbol] || activeSymbol}
          data={data?.[activeSymbol]}
          loading={loading?.[activeSymbol]}
          lastUpdate={lastUpdate?.[activeSymbol]}
          liveSymbolData={liveSpot?.[activeSymbol]}
          optionsData={liveOptions?.[activeSymbol] || {}}
          activeModules={modules}
        />
      </div>

      {settingsOpen && (
        <MobileSettingsSheet theme={theme} onClose={() => setSettingsOpen(false)} activeModules={modules} setActiveModules={setActiveModules} />
      )}
    </div>
  );
}